import { EntityStatus } from '@shared/models/menu.model';
import { PlInputType } from '@app/components/pl-input/pl-input.component';
import { HttpErrorResponse } from '@angular/common/http';

export const blockerStepFullUrl: string = '/c/c/m/page-unavailable' as const;

export enum BlockType {
  audio = 'audio',
  barChart = 'bar_chart',
  button = 'button',
  carousel = 'carousel',
  checkbox = 'checkbox',
  container = 'container',
  feedback = 'feedback',
  gaugeChart = 'gauge_chart',
  iconText = 'icon_text',
  html = 'html',
  lifeAct = 'lifeact',
  lifeChanges = 'lifeChanges',
  lifemap = 'lifemap',
  moduleOverview = 'module_overview',
  radiobox = 'radiobox',
  spiderChart = 'spider_chart',
  textarea = 'textarea',
  textbox = 'textbox',
  ul = 'ul',
  viaSurvey = 'via_survey',
  video = 'video',
  wordCloud = 'word_cloud',
}

export interface BlockAbstract {
  id: number;
  element: BlockType;
  source_program_data_code: string | null;
  program_data_code: string | null;
  data: any;
  type?: 'static' | 'dynamic'; // not used, present only with container
  customCssClass?: string[]; // created and used only by FE
}

export interface BlockUlItem {
  label: string;
  sub_label: string | null;
  takeaways: string | null;
}

export interface BlockUlItemsOld {
  [index: string]: BlockUlItem;
}

export interface BlockUlData {
  config: { showMoreButtonAtStart: boolean; minItemsDisplayed: number };
  label: string;
  placeholder: null;
  name: null;
  value: null;
  items: BlockUlItem[] | BlockUlItemsOld; // BlockUlItemsOld in potentialife-course/cycle-3/module-27/step-10-1
  itemsArr?: BlockUlItemsOld; // present in potentialife-course/cycle-3/module-27/step-10-1, not used
}

export interface BlockUl extends BlockAbstract {
  element: BlockType.ul;
  source_program_data_code: string | null;
  program_data_code: string | null;
  data: BlockUlData;
}

export interface BarDef {
  title: string;
  color: string;
  value: string;
}

export interface BlockBarChartData {
  value: null;
  name: string;
  title: string;
  bars: BarDef[];
  barWidth: number; // Between 0 & 1
}

export interface BlockBarChart extends BlockAbstract {
  element: BlockType.barChart;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockBarChartData;
}

export interface BlockGaugeChartData {
  value: null;
  name: string;
  title: string;
  gauge: { suffix_label: string; unit: null; min: string; max: string; colour: string; value: string };
}

export interface BlockGaugeChart extends BlockAbstract {
  element: BlockType.gaugeChart;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockGaugeChartData;
}

export type BlockTextBoxInputType = Exclude<PlInputType, PlInputType.password>;

export interface BlockTextboxData {
  label: string;
  placeholder: string | null;
  value: string | null;
  name: string;
  config: { type: BlockTextBoxInputType; required: '1' | '0' };
}

export interface BlockTextbox extends BlockAbstract {
  element: BlockType.textbox;
  source_program_data_code: null;
  program_data_code: string;
  data: BlockTextboxData;
}

export enum PaddingBlockHtml {
  noBgPadding = 'no-bg-padding',
}

export enum BackgroundColorBlockHtml {
  silver = 'silver',
}

export enum CssClassBlockHtml {
  paddingTopBottom = 'padding-top-bottom',
}

export interface BlockHtmlData {
  value: string;
  config?: {
    hideIfStepIsCompleted: boolean;
    padding?: PaddingBlockHtml;
    bgColor?: BackgroundColorBlockHtml;
    class?: CssClassBlockHtml;
  };
}

export interface BlockHtml extends BlockAbstract {
  element: BlockType.html;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockHtmlData;
}

export enum IconType {
  chevronBackward = 'icon-pl-lt',
  chevronForward = 'icon-pl-gt',
  exclamation = 'icon-pl-exclamation',
  eye = 'icon-pl-eye',
  lifeMap = 'icon-badge-lifemap',
  lock = 'icon-pl-lock',
  logo = 'icon-pl-logo',
  pdf = 'icon-pl-pdf',
  selfDiscovery = 'icon-badge-self-discovery',
  strengths = 'icon-badge-strengths',
  tick = 'icon-pl-tick',
  timeToReflect = 'icon-badge-time-to-reflect',
}

export enum IconColor {
  primary = 'primary',
  secondary = 'secondary',
  alert = 'alert',
  success = 'success',
  plLogoColor = 'pl-logo-color',
}

export enum ButtonType {
  link = 'link',
  pdf = 'pdf',
  state = 'state',
}

export interface BlockIconButton {
  color: IconColor;
  href: string;
  label: string;
}

export interface BlockIconTextData {
  value: null;
  icon: {
    type: IconType;
    color: IconColor | null;
  };
  text: string; // HTML
  type: ButtonType;
  button?: BlockIconButton;
}

export interface BlockIconText extends BlockAbstract {
  element: BlockType.iconText;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockIconTextData;
}

export interface Caption {
  label: string;
  lang: string;
  src: string;
}

export interface BlockVideoData {
  poster: string;
  source_hd: string;
  source_sd: string;
  captions: Caption[];
}

export interface BlockVideo extends BlockAbstract {
  element: BlockType.video;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockVideoData;
}

export interface BlockCarouselData {
  config: {
    showDots: '1' | '0';
    customCssClass: string | null; // TODO check with henrik if there is a value for this anywhere
  };
  items: string[];
}

export type RatingValue = '0' | '1' | '2' | '3' | '4';

export interface BlockCarousel extends BlockAbstract {
  element: BlockType.carousel;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockCarouselData;
}

export interface BlockFeedbackData {
  config: {
    program_data_code_feedback_text: string;
  };
  feedback_text: string | null;
  label_stars: string;
  label_text: string;
  placeholder_text: string;
  rating_value: RatingValue | null;
}

export interface BlockFeedback extends BlockAbstract {
  element: BlockType.feedback;
  source_program_data_code: null;
  program_data_code: string;
  data: BlockFeedbackData;
}

export enum LifeChangeStatus {
  notStarted = 0,
  started = 1,
  workingOnIt = 2,
  nearlyThere = 3,
  habit = 4,
}

export interface LifeChangeItem {
  id: number;
  createdAt: string;
  updatedAt: string;
  status: LifeChangeStatus;
  title: string;
}

export interface BlockLifeChangesData {
  value: string | null; // JSON.stringify of LifeChangeItem[]
}

export interface BlockLifeChanges extends BlockAbstract {
  element: BlockType.lifeChanges;
  source_program_data_code: null;
  program_data_code: string;
  data: BlockLifeChangesData;
}

export interface BlockLifeActData {
  program_data_code: string;
  program_data_code_completed: string;
  config: {
    type: string;
    heading: string;
    subBody: string;
    calendarTitle: string;
    calendarDescription: string;
  };
  value?: string;
  lifeActCompleted?: '0' | '1';
}

export interface BlockLifeAct extends BlockAbstract {
  element: BlockType.lifeAct;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockLifeActData;
}

export enum FeedbackColor {
  green = 'green',
  red = 'red',
}

export interface RadioItem {
  label: string | null;
  sub_label: string | null;
  value: string | null;
  feedback: string | null;
  color: FeedbackColor;
  selected: boolean;
}

export interface BlockRadioBoxData {
  name: string;
  label: string | null;
  subLabel: string | null;
  config: {
    required: '1' | '0' | true | false;
    minRadioBoxDisplayed: number | null;
  };
  value: string | null;
  items: RadioItem[];
}

export interface BlockRadioBox extends BlockAbstract {
  element: BlockType.radiobox;
  source_program_data_code: null;
  program_data_code: string;
  data: BlockRadioBoxData;
}

export interface BlockTextAreaData {
  label: string | null;
  placeholder: string | null;
  value: string | null;
  name: string | null;
  config: {
    class?: 'large-height';
    minNumberWords: null | number;
    required: '1' | '0';
  };
}

export interface BlockTextArea extends BlockAbstract {
  element: BlockType.textarea;
  source_program_data_code: null;
  program_data_code: string;
  data: BlockTextAreaData;
}

export const keyStrengthName: 'StrengthName' = 'StrengthName' as const;
export const keyStrengthDescription: 'StrengthDescription' = 'StrengthDescription' as const;

export interface ViaSurveyStrength {
  [keyStrengthName]: string;
  [keyStrengthDescription]: string;
}

export const TOKEN_SURVEY_QUERY_PARAM_KEY: string = 'token_survey';

export interface BlockViaSurveyData {
  intro_survey: {
    value: string | null;
  };
  intro_results: {
    value: string | null;
  };
  intro_results_2: {
    value: string | null;
  };
  during_program_title_result: {
    value: string | null;
  };
  before_program_title_result: {
    value: string | null;
  };
  before_program_after_button: {
    value: string | null;
  };
  config: {
    before_program_button_pdf: {
      href: string | null;
      label: string | null;
    };
    nb_questions_per_page: number | null;
    title_strength_list: string | null;
    strength_list_min_selected: number;
    strength_list_max_selected: number;
    all_strengths: string;
  };
  items: ViaSurveyStrength[];

  // undefined before the via_survey step (or doing via survey before the program) is completed
  // then It's a list of StrengthName to know which strength the user has selected
  value?: string; // JSON.stringify(string[])
}

export interface BlockViaSurvey extends BlockAbstract {
  element: BlockType.viaSurvey;
  source_program_data_code: string;
  program_data_code: string;
  data: BlockViaSurveyData;
}

export interface CheckboxItem {
  checked: boolean;
  feedback?: string;
  label: string;
  sub_label: string;
  value: string;
}

export interface BlockCheckboxData {
  config: {
    min_selected: number | string;
    max_selected: number | string; // string is a "number" like "3"
  };
  items: CheckboxItem[];
  label: string | null;
  name: string;
  value: null | string; // JSON.stringify of selected items value: "[\"Football tournament\",\"Friend's dsflkjds fslkj\",\"Action plan for sales conference\"]"
}

export interface BlockCheckbox extends BlockAbstract {
  element: BlockType.checkbox;
  source_program_data_code: string | null;
  program_data_code: string;
  data: BlockCheckboxData;
}

export interface BlockContainerData {
  block_ids: number[];
  blocks: Block[];
}

export interface BlockContainer extends BlockAbstract {
  element: BlockType.container;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockContainerData;
}

export interface StepOverview {
  title: string;
  name: string;
  description: string;
  status: EntityStatus;
}

export interface BlockModuleOverviewData {
  name: string;
  module_id: string;
  steps: StepOverview[];
}

export interface BlockModuleOverview extends BlockAbstract {
  element: BlockType.moduleOverview;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockModuleOverviewData;
}

export interface SpiderChartAxis {
  axis: string;
  value: `${number}`;
}

export interface BlockSpiderChartData {
  value: null;
  name: string;
  title: string;
  set: [
    {
      axes: SpiderChartAxis[];
    },
  ];
}

export interface BlockSpiderChart extends BlockAbstract {
  element: BlockType.spiderChart;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockSpiderChartData;
}

export enum ButtonColor {
  primary = 'primary',
  alert = 'alert',
  success = 'success',
  tangerine = 'Tangerine',
}

export enum ButtonPosition {
  right = 'right', // right on desktop & mobile
  left = 'left', // left on desktop and mobile
  center = 'center', // center on desktop and mobile
  leftMobile100 = 'left mobile-100', // left on desktop and take 100% width on mobile
}

export enum ButtonCategory {
  certificate = 'certificate',
  default = 'default',
}

export interface BlockButtonData {
  name: string | null;
  type: ButtonType;
  category?: ButtonCategory;
  color: ButtonColor | null | string;
  href: string | null;
  label: string | null;
  position: ButtonPosition | null | string;
  config?: {
    hideIfStepIsCompleted: '1';
  };
}

export interface BlockButton extends BlockAbstract {
  element: BlockType.button;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockButtonData;
}

export interface LifemapQuestion {
  title: string;
  helpText: string;
  answerHint: {
    '1': string;
    '2': string;
    '3': string;
    '4': string;
    '5': string;
    'N/A': string | null;
  };
}

export type AnswerHintKeys = keyof LifemapQuestion['answerHint'];

export type LevelNumber = '1' | '2' | '3';

export interface BlockLifemapData {
  config: {
    minDaysToComplete: 3 | 4 | 5 | 6 | 7;
    lifeMapNumber: LevelNumber;
    welcome: {
      title: string;
      text: string[];
    };
    stepCompleted: {
      title: string;
      text: string[];
    };
    currentProgressLabel: string;
    continueButtonLabel: string;
    lifeMapCompleteStickyFooterTitle: string;
    discardEditEntry: {
      title: string;
      subTitle: string;
    };
    finishLifeMap: {
      title: string;
      subTitle: string;
    };
    entries: {
      createNewText: string;
      saveLifeMap: string;
      createdToast: string;
      editedToast: string;
      selectNext: string;
      selectStartTimeLabel: string;
      selectEndTimeLabel: string;
      lifeMapEntryLabel: string;
      lifeMapEntryPlaceholder: string | null;
      chooseCategoryLabel: string;
      timeslotSelectedLabel: string;
      pastActivitiesLabel: string;
    };
    help: {
      title: string;
      tips: (string | null)[];
    };
    overwrite: {
      title: string;
      subTitle: string;
    };
    helpQuestionsLabel: string;
    questions: LifemapQuestion[];
  };
}

export interface BlockLifemap extends BlockAbstract {
  element: BlockType.lifemap;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockLifemapData;
}

export interface WordCloudItem {
  label: null;
  sub_label: null;
  value: null;
}

export interface WordData {
  text: string;
  power: number;
}

export interface BlockWordCloudData {
  value: string; // Not used
  items: WordCloudItem[]; // Not used
  wordData: WordData[];
}

export interface BlockWordCloud extends BlockAbstract {
  element: BlockType.wordCloud;
  source_program_data_code: string; // Not used
  program_data_code: string; // Not used
  data: BlockWordCloudData;
}

export interface BlockAudioData {
  title: string;
  link: string;
}

export interface BlockAudio extends BlockAbstract {
  element: BlockType.audio;
  source_program_data_code: null;
  program_data_code: null;
  data: BlockAudioData;
}

export type Block =
  | BlockAudio
  | BlockBarChart
  | BlockButton
  | BlockCarousel
  | BlockContainer
  | BlockCheckbox
  | BlockFeedback
  | BlockGaugeChart
  | BlockHtml
  | BlockIconText
  | BlockLifeAct
  | BlockLifeChanges
  | BlockLifemap
  | BlockModuleOverview
  | BlockRadioBox
  | BlockSpiderChart
  | BlockTextArea
  | BlockTextbox
  | BlockUl
  | BlockViaSurvey
  | BlockVideo
  | BlockWordCloud;

export interface StepData {
  id: number;
  title: string;
  name: string;
  description: string;
  congrats: string;
  hideStepInMenu: boolean;
  breadcrumbs: string;
  prev_page_url: string | null;
  next_page_url: string | null;
  fullUrl: string;
  slug: string;
  status: EntityStatus;
  blocks: (Block | null)[];
}

export interface ErrorStepData {
  error: HttpErrorResponse;
}

export function isStepData(stepData: StepData | ErrorStepData | null): stepData is StepData {
  return !!(stepData && stepData.constructor === Object && stepData.hasOwnProperty('id'));
}

export function isErrorStepData(stepData: StepData | ErrorStepData | null): stepData is ErrorStepData {
  return !!(stepData && stepData.constructor === Object && stepData.hasOwnProperty('error'));
}
